import axios from 'axios';
import { get } from 'lodash';

import { SearchRequest } from 'core/util/Search';

export interface Source {
    id: string;
    uid?: number;
    name: string;
    largeIconPath: string;
    smallIconPath: string;
    iconPath?: string;
    oauthable: boolean;
    credentialHierarchy: CredentialHierarchy;
    credential: Record<string, any>;
    social: Record<string, any>;
    review: Record<string, any>;
    message: Record<string, any>;
    survey: Record<string, any>;
    listing?: any;
    listable?: boolean;
    publishable?: boolean;
    listingPublishConfig: { [key: string]: boolean };
    attributes: string[];
    publishResponses?: number;
    countries?: string[];
    industries?: any[];
    reviewable?: boolean;
    sociable?: boolean;
    pageMetric?: any;
    status?: number;
    requestReviewable?: boolean;
    syndicationSource?: boolean;
    reviewResponseConfig?: any;
    postMetric?: any;
    adMetric?: any;
    socialAds?: any;
    collectable?: boolean;
    overallRS: RatingScale;
    requireCredentials?: boolean;
}

export enum ReviewResponseApiType {
    OAuth = 1,
    Token = 2,
    Password = 3,
    Manual = 4,
}

enum RatingBucket {
    VERY_BAD = 1,
    BAD = 2,
    NEUTRAL = 3,
    GOOD = 4,
    VERY_GOOD = 5,
    NONE = 6,
}

interface RatingScale {
    min: number;
    max: number;
    overallMin: number;
    overallMax: number;
    bucketUpperBounds: { [key in RatingBucket]: number };
    collectable?: boolean;
}

export enum CredentialHierarchy {
    None = 0,
    System = 1,
    Tenant = 2,
    Location = 3,
}

export enum ListingField {
    Name = 0,
    BusinessHours = 1,
    Address = 2,
    Category = 3,
    Description = 4,
    Photo = 5,
    Insurances = 6,
    FAQ = 7,
    Menu = 8,
    Product = 9,
    Service = 10,
    Phone = 11,
    Web = 12,
    Attributes = 13,
    MoreHours = 14,
}

export enum ListingFieldPublishOption {
    Sync = 0,
    Delete = 1,
    AutoCrop = 2,
}

type SupportedFieldOptionType = {
    [key: string]: boolean;
};

export type SupportedFieldType = {
    label: string;
    key: number;
    options: SupportedFieldOptionType;
};

export enum SourceId {
    Email = 'EMAIL',
    Facebook = 'FACEBOOK',
    Google = 'GOOGLE_PLACES',
    Youtube = 'YOUTUBE',
    // GoogleQA = 'GOOGLE_QA', // Deprecated: Google My Business Q&A API discontinued on November 3, 2025
    Instagram = 'INSTAGRAM',
    SMS = 'SMS',
    Survey = 'SURVEY',
    Twitter = 'TWITTER',
    Titktok = 'TIKTOK',
    WhatsApp = 'WHATSAPP',
    LinkedIn = 'LINKEDIN',
    Yelp = 'YELP',
    SNAPCHAT = 'SNAPCHAT',
    HealthGrades = 'HEALTH_GRADES',
    DealerRater = 'DEALER_RATER',
    Booking = 'BOOKING',
    Carfax = 'CARFAX',
    Edmunds = 'EDMUNDS',
    ApartmentGuide = 'APARTMENT_GUIDE',
    Rent = 'RENT',
    Apple = 'APPLE',
    Threads = 'THREADS',
    ApartmentRatings = 'APARTMENT_RATINGS',
    UberEats = 'UBER_EATS',
}

export const NonOauthSources: Record<string, string> = {
    SNAPCHAT: 'SNAPCHAT',
    HEALTH_GRADES: 'HEALTH_GRADES',
    DEALER_RATER: 'DEALER_RATER',
    BOOKING: 'BOOKING',
    CARFAX: 'CARFAX',
    EDMUNDS: 'EDMUNDS',
    APARTMENT_GUIDE: 'APARTMENT_GUIDE',
    RENT: 'RENT',
};

export const hashtagSearchUrl: Record<string, string> = {
    [SourceId.Facebook]: 'https://www.facebook.com/hashtag/',
    [SourceId.Instagram]: 'https://www.instagram.com/explore/tags/',
    [SourceId.Twitter]: 'https://twitter.com/search?q=',
    [SourceId.LinkedIn]: 'https://www.linkedin.com/feed/hashtag/',
};

export enum SourceUid {
    SMS = 2580,
    Survey = 1900,
}

export enum PublishResponses {
    None = 0,
    Manual = 1,
    API = 2,
    Meru = 3,
}

export enum FieldName {
    Id = '__source_id__',
    AutofindCapability = '__source_autofind_capability__',
    CredentialHierarchy = '', // TODO
    IconPath = '__source_icon_path__',
    MessageCapability = '__source_message_capability__',
    Name = '__source_name__',
    SocialCapability = '__source_social_capability__',
    OAuthable = '__source_oauthable__',
    RequireCredentials = '__source_requireCredentials__',
    PublishResponses = '__source_publish_responses__',
    ReviewCapability = '__source_reivew_capability__',
}

export interface GetSourcesArgs {
    basic?: boolean;
    moduleIds?: string[];
}
export type ConfigField = {
    field: ListingField;
    option: ListingFieldPublishOption;
    value: boolean;
};

export type ConfigList = {
    [key: string]: ConfigField;
};

export interface SourceConfig {
    id: string;
    agencyId: number;
    tenantId: number;
    source: string;
    isDefault?: boolean;
    fieldConfigs: ConfigField[];
}

export const getSources = async (
    agencyId: number,
    tenantId: number,
    search?: SearchRequest,
    args?: GetSourcesArgs,
): Promise<Array<Source>> => {
    const params: any = { agencyId, tenantId, search, args };

    const { data } = await axios.get(`/api/tenants/${tenantId}/sources`, { params });

    return data;
};

export const getSource = async (
    agencyId: number,
    tenantId: number,
    sourceId: string,
    args?: GetSourcesArgs,
): Promise<Source> => {
    const params: any = { agencyId, tenantId, sourceId, args };

    const { data } = await axios.get(`/api/tenants/${tenantId}/sources/${sourceId}`, { params });

    return data;
};

export const saveSourceListingConfig = async (tenantId: number, sourceId: string, config: any): Promise<any> => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/sources/${sourceId}/source-listing-config`, config);

    return data;
};

export const searchSourceListingConfig = async (
    tenantId: number,
    search?: SearchRequest,
    args?: GetSourcesArgs,
): Promise<SourceConfig[]> => {
    const params: any = { tenantId, args, search };
    const { data } = await axios.get(`/api/tenants/${tenantId}/search-listing-config`, { params });

    return data;
};

export const getDefaultListingSyncConfig = async (
    tenantId: number,
    sourceId: string,
    search?: SearchRequest,
    args?: GetSourcesArgs,
): Promise<ConfigField[]> => {
    const params: any = { tenantId, sourceId, args };

    const { data } = await axios.get(`/api/tenants/${tenantId}/sources/${sourceId}/get-default-listings`, { params });

    return data;
};

export const syncToSource = async (tenantId: number, locationUid: string, sourceID: string): Promise<any> => {
    const params: any = { tenantId, locationUid, sourceID };

    const { data } = await axios.post(`/api/tenants/${tenantId}/profile-publish/sync-to-source`, params);

    return data;
};

export const getCredentialsForLocationuid = async (
    locationUid: string | undefined,
    tenantId: string,
    sourceID: string,
    credentialType: string,
): Promise<Location[]> => {
    const { data } = await axios.get(
        `/api/tenants/${tenantId}/getCredentialForLocationByUid?credentialType=${credentialType}&locationUid=${locationUid}&sourceID=${sourceID}`,
    );

    return data;
};

const credentialHierarchyLabels: Record<CredentialHierarchy, string | undefined> = {
    [CredentialHierarchy.System]: 'REPUTATION',
    [CredentialHierarchy.Tenant]: 'TENANT',
    [CredentialHierarchy.Location]: 'LOCATION',
    [CredentialHierarchy.None]: undefined,
};

export const getCredentialHierarchyLabel = (credentialHierarchy: CredentialHierarchy): string | undefined => {
    return get(credentialHierarchyLabels, credentialHierarchy, undefined);
};
